import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../assets/images/close-icon.svg';

const FEEDBACK_CATEGORIES = [
  'Design is off',
  'Functionality is broken',
  'Forgot what I told it before',
  'Other',
] as const;

interface FeedbackCommentModalProps {
  open: boolean;
  comment: string;
  category?: string | null;
  onCommentChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  isLoading: boolean;
  error?: string | null;
}

const FeedbackCommentModal = ({
  open,
  comment,
  category,
  onCommentChange,
  onCategoryChange,
  onClose,
  onSubmit,
  isLoading,
  error,
}: FeedbackCommentModalProps) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-[540px] bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden">
        <div className="relative p-6">
          {!isLoading ? (
            <button
              className="absolute right-4 top-4 text-white/40 hover:text-white/80 transition-colors"
              onClick={onClose}
              type="button"
              title="Close"
            >
              <CloseIcon width="14" height="14" />
            </button>
          ) : null}

          <h2 className="text-xl font-semibold text-white font-sora">
            Help us improve
          </h2>

          <p className="mt-5 text-sm text-white/60">
            Please tell us why this response wasn't helpful to you.
          </p>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {FEEDBACK_CATEGORIES.map((option) => {
              const isSelected = category === option;

              return (
                <button
                  key={option}
                  type="button"
                  disabled={isLoading}
                  onClick={() => onCategoryChange(option)}
                  className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
                    isSelected
                      ? 'border-[#208AEC] bg-[#208AEC]/10 text-white shadow-[0_0_0_1px_rgba(32,138,236,0.35)]'
                      : 'border-white/10 bg-black/20 text-white/80 hover:border-white/20 hover:bg-white/5'
                  }`}
                >
                  {option}
                </button>
              );
            })}
          </div>

          <p className="mt-5 text-sm text-white/60">
            Additional feedback
          </p>

          <textarea
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            disabled={isLoading}
            placeholder="Type your feedback here..."
            className="mt-3 w-full min-h-[140px] rounded-xl bg-black/20 border border-white/10 text-white placeholder:text-white/30 px-4 py-3 outline-none resize-none focus:border-white/20"
          />

          {error ? (
            <div className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
              {error}
            </div>
          ) : null}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors"
              type="button"
              onClick={onClose}
              disabled={isLoading}
            >
              Skip
            </button>

            <button
              className="px-4 py-2 rounded-xl text-white font-medium transition-colors disabled:opacity-60"
              style={{ backgroundColor: '#27AE60' }}
              type="button"
              disabled={isLoading}
              onClick={() => {
                void onSubmit();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#19875F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#27AE60';
              }}
            >
              {isLoading ? 'Sending...' : 'Send feedback'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FeedbackCommentModal;